import clsx from 'clsx'
import { forwardRef, useId, useImperativeHandle, useRef } from 'react'
import { FormComponentBodySlotUI } from '../FormComponentUI/FormComponentBodySlotUI'
import { FormComponentBodyUI } from '../FormComponentUI/FormComponentBodyUI'
import { FormComponentErrorMessageUI } from '../FormComponentUI/FormComponentErrorMessageUI'
import { FormComponentLabelUI } from '../FormComponentUI/FormComponentLabelUI'
import { FormComponentWrapperUI } from '../FormComponentUI/FormComponentWrapperUI'
import styles from './Input.module.scss'
import { IInputProps } from './types'

const Input = forwardRef<HTMLInputElement, IInputProps>(
  (
    {
      error,
      label,
      size = 'md',
      slots,
      transparent = false,
      disabled,
      required,
      className,
      style,
      placeholder,
      id: propId,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId()
    const id = propId || generatedId
    const inputRef = useRef<HTMLInputElement>(null)

    const isError = Boolean(error)
    const errorMessageId = typeof error === 'string' ? `${id}-err` : undefined

    const handleBodyClick = (e: React.MouseEvent<HTMLDivElement>) => {
      // focus input only if clicked somewhere in the body, not slots
      if (e.target === e.currentTarget) inputRef.current?.focus()
    }

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

    return (
      <FormComponentWrapperUI
        size={size}
        disabled={disabled}
        className={className}
        style={style}
        isError={isError}
      >
        {label ? (
          <FormComponentLabelUI size={size} required={required} htmlFor={id}>
            {label}
          </FormComponentLabelUI>
        ) : null}
        <FormComponentBodyUI
          isTransparent={transparent}
          size={size}
          isError={isError}
          canGrowVertically={false}
          className={styles.body}
          onClick={handleBodyClick}
        >
          {slots?.before && <FormComponentBodySlotUI>{slots.before}</FormComponentBodySlotUI>}
          <input
            className={clsx(styles.input, 'mui-input')}
            data-testid="mui-input"
            ref={inputRef}
            placeholder={placeholder}
            id={id}
            required={required}
            disabled={disabled}
            aria-invalid={isError}
            aria-errormessage={errorMessageId}
            {...props}
          />
          {slots?.after && <FormComponentBodySlotUI>{slots.after}</FormComponentBodySlotUI>}
        </FormComponentBodyUI>
        {errorMessageId ? (
          <FormComponentErrorMessageUI id={errorMessageId}>{error}</FormComponentErrorMessageUI>
        ) : null}
      </FormComponentWrapperUI>
    )
  },
)

Input.displayName = 'Input'

export default Input
